import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  checkInSuccess,
  checkOutSuccess,
  setLoading,
  clearError,
  selectActiveCheckIns,
} from "../redux/slices/checkInSlice";
import { logout } from "../redux/slices/authSlice";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  LogOut,
  BarChart2,
  Clock,
  MapPin,
  ShoppingBag,
  AlertCircle,
  RefreshCw,
  Navigation,
  Search,
  User,
} from "lucide-react";
import HistoryView from "../components/SalesExecutive/HistoryView";
import RecordSale from "./RecordSale";
import ProfileView from "../components/SalesExecutive/ProfileView";
import RecordDisplayProof from "./RecordDisplayProof";
import FullPageLoader from "../components/Common/FullPageLoader";
import "./SalesExecutive.css";
import { selectUser, selectAuthLoading } from "../redux/slices/authSlice";
import {
  api,
  createCheckIn,
  checkoutCheckIn,
  getActiveCheckIn,
  getMmTaskTypes,
} from "../services/api";
import { isMerchandiser } from "../constants/roles";
import { useNotification } from "../hooks/useNotification";

const ACTIVE_CHECKIN_CACHE_KEY = "se_active_checkin_cache_v1";
const ACTIVE_CHECKIN_TIMER_SEED_KEY = "se_active_checkin_timer_seed_v1";
const ACTIVE_CHECKIN_CACHE_MAX_AGE_MS = 60 * 1000;
const ADDRESS_LOOKUP_FALLBACK = "GPS captured, address lookup unavailable";
const LOCATION_COORDINATE_DECIMALS = 6;
const SESSION_STATUS = Object.freeze({
  ACTIVE: "active",
  BREAK: "break",
  IDLE: "idle",
});
const DEFAULT_ROADSHOW_EVENT_DETAILS = "Roadshow";

const toOptionalPositiveInt = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const toBooleanFlag = (value, fallbackText = null) => {
  if (value === true || value === false) return value;
  if (typeof value === "number") return value > 0;
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "1" || normalized === "true" || normalized === "t") return true;
  if (normalized === "0" || normalized === "false" || normalized === "f") return false;
  return String(fallbackText || "").trim().toLowerCase() === "roadshow";
};

const isRoadshowRecord = (record) => {
  if (!record || typeof record !== "object") return false;
  return toBooleanFlag(
    record.isEvent ?? record.is_event ?? null,
    record.eventDetails ?? record.event_details ?? "",
  );
};

const toTimestamp = (value) => {
  if (value == null) return null;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && value.trim() !== "") {
      return numeric;
    }
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeCheckInTimestamp = (timestamp, rawValue) => {
  if (!Number.isFinite(timestamp)) return null;

  const now = Date.now();
  const maxFutureDriftMs = 5 * 60 * 1000;

  // Neutralize small server/client clock skew so timer does not flash 00:00:00.
  if (timestamp > now && timestamp - now <= maxFutureDriftMs) {
    return now;
  }

  if (timestamp <= now + maxFutureDriftMs) {
    return timestamp;
  }

  // Some environments return local DB time serialized with "Z" (UTC marker),
  // which shifts parsed timestamps into the future by local UTC offset.
  const rawString = typeof rawValue === "string" ? rawValue.trim() : "";
  if (rawString.endsWith("Z")) {
    const corrected = timestamp + new Date().getTimezoneOffset() * 60 * 1000;
    if (Number.isFinite(corrected) && corrected <= now + maxFutureDriftMs) {
      return corrected;
    }
  }

  return timestamp;
};

const getCheckInStartTimestamp = (checkInRecord) => {
  if (!checkInRecord) return null;
  const checkInId = Number(checkInRecord.check_in_id || checkInRecord.checkInId);
  const timerSeed = readActiveCheckInTimerSeed();
  const seededStart =
    timerSeed && Number.isFinite(checkInId) && timerSeed.checkInId === checkInId
      ? timerSeed.startedAtMs
      : null;

  const candidates = [
    checkInRecord.checked_in_at,
    checkInRecord.checkedInAt,
    checkInRecord.check_in_at,
    checkInRecord.created_at,
    checkInRecord.createdAt,
  ];

  for (const candidate of candidates) {
    const parsed = toTimestamp(candidate);
    if (!Number.isFinite(parsed)) continue;
    const normalized = normalizeCheckInTimestamp(parsed, candidate);
    if (Number.isFinite(seededStart)) {
      return Math.min(normalized, seededStart);
    }
    return normalized;
  }

  if (Number.isFinite(seededStart)) {
    return seededStart;
  }

  return null;
};

const readCachedActiveCheckIn = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_CHECKIN_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const cachedAtMs = Number(parsed.__cachedAtMs || 0);
    if (!Number.isFinite(cachedAtMs) || Date.now() - cachedAtMs > ACTIVE_CHECKIN_CACHE_MAX_AGE_MS) {
      clearCachedActiveCheckIn();
      return null;
    }
    if (parsed.checked_out_at || parsed.checkedOutAt) return null;
    if (!getCheckInStartTimestamp(parsed)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const writeCachedActiveCheckIn = (value) => {
  try {
    if (!value || typeof value !== "object") return;
    localStorage.setItem(
      ACTIVE_CHECKIN_CACHE_KEY,
      JSON.stringify({
        ...value,
        __cachedAtMs: Date.now(),
      }),
    );
  } catch (_) {
    // ignore cache write failures
  }
};

const clearCachedActiveCheckIn = () => {
  try {
    localStorage.removeItem(ACTIVE_CHECKIN_CACHE_KEY);
  } catch (_) {
    // ignore cache clear failures
  }
};

const readActiveCheckInTimerSeed = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_CHECKIN_TIMER_SEED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const checkInId = Number(parsed.checkInId);
    const startedAtMs = Number(parsed.startedAtMs);
    if (!Number.isFinite(checkInId) || !Number.isFinite(startedAtMs)) {
      return null;
    }
    return { checkInId, startedAtMs };
  } catch (_) {
    return null;
  }
};

const writeActiveCheckInTimerSeed = (checkInId, startedAtMs = Date.now()) => {
  try {
    const normalizedCheckInId = Number(checkInId);
    const normalizedStart = Number(startedAtMs);
    if (!Number.isFinite(normalizedCheckInId) || !Number.isFinite(normalizedStart)) {
      return;
    }
    localStorage.setItem(
      ACTIVE_CHECKIN_TIMER_SEED_KEY,
      JSON.stringify({
        checkInId: normalizedCheckInId,
        startedAtMs: normalizedStart,
      }),
    );
  } catch (_) {
    // ignore cache write failures
  }
};

const clearActiveCheckInTimerSeed = () => {
  try {
    localStorage.removeItem(ACTIVE_CHECKIN_TIMER_SEED_KEY);
  } catch (_) {
    // ignore cache clear failures
  }
};

const toVisitState = (checkInRecord, currentLocation = null) => {
  if (!checkInRecord) return null;
  const checkedInAt = getCheckInStartTimestamp(checkInRecord) || Date.now();

  return {
    startTime: Number.isFinite(checkedInAt) ? checkedInAt : Date.now(),
    location:
      checkInRecord.outlet_name ||
      checkInRecord.outlet?.bp_outlet_name ||
      "Unknown outlet",
    address:
      checkInRecord.outlet_address ||
      checkInRecord.outlet?.addresses?.[0]?.street ||
      "",
    coords: currentLocation,
    isEvent: isRoadshowRecord(checkInRecord),
    eventDetails: checkInRecord.event_details || "",
  };
};

const reverseGeocodeLocation = async (latitude, longitude) => {
  const zoomLevels = [18, 16, 14];

  for (const zoom of zoomLevels) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=${zoom}&addressdetails=1`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Accept-Language": "en-MY,en",
          },
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.warn(`Address lookup failed at zoom ${zoom}: HTTP ${response.status}`);
        continue;
      }

      const data = await response.json();
      const displayName =
        typeof data?.display_name === "string" ? data.display_name.trim() : "";

      if (displayName) {
        return displayName;
      }
    } catch (fetchError) {
      console.warn(`Address lookup failed at zoom ${zoom}:`, fetchError);
    }
  }

  return ADDRESS_LOOKUP_FALLBACK;
};

export default function SalesExecutiveApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { showSuccess } = useNotification();

  // Redux Hooks
  const dispatch = useDispatch();
  // User state
  const user = useSelector(selectUser);
  const userLoading = useSelector(selectAuthLoading);
  // Checkin state
  const activeCheckIns = useSelector(selectActiveCheckIns);

  // Local State
  const [checkInState, setCheckInState] = useState("IDLE"); // IDLE, BREAK, LOCATING, ERROR_GPS, ERROR_NO_OUTLETS, SELECT_OUTLET, CHECKED_IN
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationErrorDetails, setLocationErrorDetails] = useState(null);
  const [view, setView] = useState("DASHBOARD");
  const [salesEntrySource, setSalesEntrySource] = useState("nav");
  const [salesMode, setSalesMode] = useState("sale");
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [timerNow, setTimerNow] = useState(() => Date.now());
  const [todayStats, setTodayStats] = useState({ sales: 0, visits: 0 });
  const [loadingTodayStats, setLoadingTodayStats] = useState(false);
  const [loadingActiveCheckIn, setLoadingActiveCheckIn] = useState(true);
  const [sessionStatus, setSessionStatus] = useState(SESSION_STATUS.IDLE);
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [pausedDurationSeconds, setPausedDurationSeconds] = useState(0);

  const [welcomeVisible, setWelcomeVisible] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);

  // Outlet search state
  const [outletSearch, setOutletSearch] = useState("");
  const [outlets, setOutlets] = useState([]);
  const [loadingOutlets, setLoadingOutlets] = useState(false);
  const [outletError, setOutletError] = useState(null);
  const searchTimer = useRef(null);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [checkoutReason, setCheckoutReason] = useState("");
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState("");

  // Event check-in state
  const [isEventCheckIn, setIsEventCheckIn] = useState(false);
  const [eventDetails, setEventDetails] = useState("");


  // merchandiser need to pick the task, cannot check in without one
  const isMerchandiserUser = isMerchandiser(user?.role);
  const [mmTaskTypes, setMmTaskTypes] = useState([]);
  const [loadingMmTaskTypes, setLoadingMmTaskTypes] = useState(false);
  const [mmTaskTypesError, setMmTaskTypesError] = useState(null);
  const [selectedMmTask, setSelectedMmTask] = useState("");

  // New outlet state
  const [isNewOutlet, setIsNewOutlet] = useState(false);
  const [newOutletName, setNewOutletName] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [activeVisit, setActiveVisit] = useState(null);

  // Admin-managed MM task list, loaded once for merchandisers.
  useEffect(() => {
    if (!isMerchandiserUser) {
      setMmTaskTypes([]);
      setSelectedMmTask("");
      return undefined;
    }

    let cancelled = false;

    const loadMmTaskTypes = async () => {
      try {
        setLoadingMmTaskTypes(true);
        setMmTaskTypesError(null);
        const response = await getMmTaskTypes();
        if (cancelled) return;
        setMmTaskTypes(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (cancelled) return;
        setMmTaskTypes([]);
        setMmTaskTypesError(
          t("se.dialog.mmTaskLoadFailed", {
            defaultValue: "Could not load MM tasks. Please try again.",
          }),
        );
      } finally {
        if (!cancelled) setLoadingMmTaskTypes(false);
      }
    };

    loadMmTaskTypes();
    return () => {
      cancelled = true;
    };
  }, [isMerchandiserUser, t]);

  const buildTodayRange = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const loadTodayStats = useCallback(async () => {
    if (!user?.user_id) return;

    try {
      setLoadingTodayStats(true);
      const { startDate, endDate } = buildTodayRange();
      const [salesResponse, checkInHistoryResponse] = await Promise.all([
        api.get(`/sales/stats?startDate=${startDate}&endDate=${endDate}`),
        api.get(
          `/checkins/history?startDate=${startDate}&endDate=${endDate}&limit=1&offset=0`,
        ),
      ]);

      const salesToday = Number(salesResponse?.data?.stats?.total_sales || 0);
      const visitsToday = Number(checkInHistoryResponse?.data?.total || 0);

      setTodayStats({
        sales: Number.isFinite(salesToday) ? salesToday : 0,
        visits: Number.isFinite(visitsToday) ? visitsToday : 0,
      });
    } catch (error) {
      console.error("Failed to load today stats:", error);
    } finally {
      setLoadingTodayStats(false);
    }
  }, [user?.user_id]);

  // Apply one-time target view from navigation state and clear it,
  // so unrelated rerenders (like language switch) cannot replay it.
  useEffect(() => {
    if (!location.state?.view) return;

    setView(location.state.view);
    if (location.state.view === "SALES") {
      setSalesEntrySource(location.state.salesEntrySource || "nav");
    }

    const nextState = { ...location.state };
    delete nextState.view;
    delete nextState.salesEntrySource;
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(nextState).length ? nextState : {},
    });
  }, [location.state, location.pathname, navigate]);

  // Check for success message from navigation state
  useEffect(() => {
    if (location.state?.saleSaved) {
      setSuccessMessage(t("se.sale.successSaved"));
      loadTodayStats();
      setView("SALES");
      setSalesMode("sale");
      setSalesEntrySource("nav");
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
      // Auto-hide after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } else if (location.state?.returnSaved) {
      setSuccessMessage(t("se.sale.successReturnSaved"));
      loadTodayStats();
      setView("SALES");
      setSalesMode("return");
      setSalesEntrySource("nav");
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
      // Auto-hide after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } else if (location.state?.saleDeleted) {
      setSuccessMessage(t("se.sale.successDeleted"));
      loadTodayStats();
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
      // Auto-hide after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  }, [location.state, navigate, location.pathname, loadTodayStats, t]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const getHeaderTitle = () => {
    switch (view) {
      case "HISTORY":
        return t("se.header.visitHistory");
      case "PROFILE":
        return t("se.header.myProfile");
      case "SALES":
        return t("se.header.sales");
      case "DISPLAY_PROOF":
        return t("se.header.displayProof");
      case "DISPLAY_PROOF_TAB":
        return t("se.header.displayProof");
      default:
        return t("se.header.default");
    }
  };

  const shouldShowSalesBackButton =
    view === "SALES" && salesEntrySource === "dashboard";

  useEffect(() => {
    const start = getCheckInStartTimestamp(activeCheckIns);
    if (!activeCheckIns || !start) {
      setTimerNow(Date.now());
      return undefined;
    }

    // Refresh immediately so timer shows current value as soon as state becomes CHECKED_IN.
    setTimerNow(Date.now());
    const intervalId = setInterval(() => {
      setTimerNow(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [activeCheckIns]);

  const applySessionPayload = useCallback(
    (payload = {}) => {
      const nextSessionStatus =
        payload?.session_status ||
        (payload?.data ? SESSION_STATUS.ACTIVE : SESSION_STATUS.IDLE);
      const nextResumeCandidate = payload?.resume_candidate || null;
      const nextDurationSeconds =
        Number.parseInt(
          payload?.total_work_duration_seconds ||
            payload?.data?.total_work_duration_seconds ||
            0,
          10,
        ) || 0;

      setSessionStatus(nextSessionStatus);
      setResumeCandidate(nextResumeCandidate);
      setPausedDurationSeconds(nextDurationSeconds);

      if (payload?.data) {
        dispatch(checkInSuccess(payload.data));
        writeCachedActiveCheckIn(payload.data);
        writeActiveCheckInTimerSeed(
          payload?.data?.check_in_id || payload?.data?.checkInId,
        );
        return;
      }

      dispatch(checkOutSuccess());
      clearCachedActiveCheckIn();
      clearActiveCheckInTimerSeed();
    },
    [dispatch],
  );

  useEffect(() => {
    loadTodayStats();
  }, [loadTodayStats]);

  useEffect(() => {
    // Keep welcome visible while idle, acquiring location, or checked in.
    // Hide immediately when showing outlet selection or on errors.
    if (
      checkInState === "IDLE" ||
      checkInState === "BREAK" ||
      checkInState === "LOCATING" ||
      checkInState === "CHECKED_IN"
    ) {
      setWelcomeVisible(true);
    } else {
      setWelcomeVisible(false);
    }
  }, [checkInState]);

  useEffect(() => {
    if (activeCheckIns) {
      setCheckInState("CHECKED_IN");
      setActiveVisit(toVisitState(activeCheckIns, currentLocation));
      setSessionStatus(SESSION_STATUS.ACTIVE);
    } else {
      setCheckInState(sessionStatus === SESSION_STATUS.BREAK ? "BREAK" : "IDLE");
      setActiveVisit(null);
    }
  }, [activeCheckIns, currentLocation, sessionStatus]);

  useEffect(() => {
    let isMounted = true;

    const loadActiveCheckIn = async () => {
      if (!user) {
        setLoadingActiveCheckIn(false);
        clearCachedActiveCheckIn();
        clearActiveCheckInTimerSeed();
        return;
      }

      const cachedActive = readCachedActiveCheckIn();
      if (cachedActive) {
        dispatch(checkInSuccess(cachedActive));
        setSessionStatus(SESSION_STATUS.ACTIVE);
      }
      try {
        setLoadingActiveCheckIn(true);
        dispatch(setLoading(true));
        const result = await getActiveCheckIn();
        if (!isMounted) return;

        if (result?.success) {
          applySessionPayload(result);
        } else {
          applySessionPayload({
            success: false,
            session_status: SESSION_STATUS.IDLE,
          });
        }
      } catch (error) {
        if (!isMounted) return;
        applySessionPayload({
          success: false,
          session_status: SESSION_STATUS.IDLE,
        });
      } finally {
        if (isMounted) {
          setLoadingActiveCheckIn(false);
          dispatch(setLoading(false));
        }
      }
    };

    loadActiveCheckIn();
    return () => {
      isMounted = false;
    };
  }, [applySessionPayload, dispatch, user]);

  const fetchOutlets = useCallback(async ({ q = "", limit = 20 }) => {
    try {
      setLoadingOutlets(true);
      setOutletError(null);

      const params = new URLSearchParams();
      const locationPostcodeMatch = String(currentLocation?.address || "").match(/\b\d{5}\b/);
      const locationPostcode = locationPostcodeMatch ? locationPostcodeMatch[0] : "";
      if (q && q.trim()) params.append("q", q.trim());
      if (locationPostcode) params.append("postcode", locationPostcode);
      params.append("limit", limit);

      const token = localStorage.getItem("token");
      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined;

      const response = await fetch(`/api/outlets?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        setOutletError(`HTTP ${response.status}: ${response.statusText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setOutlets(result.data);
      } else {
        setOutlets([]);
        setOutletError(
          q && q.trim()
            ? `No outlets found for "${q.trim()}"`
            : "No outlets available",
        );
      }
    } catch (error) {
      console.error("Error fetching outlets:", error);
      setOutletError(error.message || "Failed to fetch outlets");
    } finally {
      setLoadingOutlets(false);
    }
  }, [currentLocation?.address]);

  useEffect(() => {
    if (checkInState !== "SELECT_OUTLET") return;

    if (searchTimer.current) clearTimeout(searchTimer.current);

    searchTimer.current = setTimeout(() => {
      const q = outletSearch.trim();
      fetchOutlets({ q, limit: 20 });
    }, 300);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [outletSearch, checkInState, fetchOutlets]);

  // Ensure initial outlets are loaded when entering SELECT_OUTLET
  useEffect(() => {
    if (checkInState === "SELECT_OUTLET" && outlets.length === 0) {
      fetchOutlets({ limit: 20 });
    }
  }, [checkInState, outlets.length, fetchOutlets]);

  const startCheckInProcess = async () => {
    setCheckInState("LOCATING");
    setLocationErrorDetails(null);
    setOutletError(null);
    setLoadingOutlets(true);

    // Try to get location (but don't fail if it doesn't work)
    try {
      if (!navigator.geolocation) {
        // No geolocation - show "Unable to locate you"
        setCurrentLocation({
          lat: null,
          lng: null,
          address: null,
          accuracy: null,
          capturedAt: null,
        });
        setCheckInState("SELECT_OUTLET");
        return;
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude, accuracy } = position.coords;

      const normalizedLatitude = Number(latitude.toFixed(LOCATION_COORDINATE_DECIMALS));
      const normalizedLongitude = Number(longitude.toFixed(LOCATION_COORDINATE_DECIMALS));
      const address = await reverseGeocodeLocation(
        normalizedLatitude,
        normalizedLongitude,
      );

      setCurrentLocation({
        lat: normalizedLatitude,
        lng: normalizedLongitude,
        address,
        accuracy:
          Number.isFinite(accuracy) && accuracy >= 0
            ? Number(accuracy.toFixed(2))
            : null,
        capturedAt: new Date(position.timestamp || Date.now()).toISOString(),
      });

      await fetchOutlets({ limit: 20 });
      setCheckInState("SELECT_OUTLET");
    } catch (error) {
      console.warn("Location detection failed, continuing without location");
      // Set location to null - will show "Unable to locate you"
      setCurrentLocation({
        lat: null,
        lng: null,
        address: null,
        accuracy: null,
        capturedAt: null,
      });
    }

    // Always proceed to SELECT_OUTLET (outlets already loaded)
    setCheckInState("SELECT_OUTLET");
  };

  const handleOutletClick = (outlet) => {
    setSelectedOutlet(outlet);
    const isResumeToSameOutlet =
      isRoadshowRecord(resumeCandidate) &&
      (
        String(resumeCandidate?.outlet_id || "") ===
          String(outlet?.bp_outlet_id || outlet?.id || "") ||
        String(resumeCandidate?.outlet_name || "").trim().toLowerCase() ===
          String(outlet?.name || outlet?.bp_outlet_name || "").trim().toLowerCase()
      );

    setIsEventCheckIn(isResumeToSameOutlet);
    setEventDetails(isResumeToSameOutlet ? DEFAULT_ROADSHOW_EVENT_DETAILS : "");
    dispatch(clearError());
    setShowConfirmDialog(true);
  };

  const handleCancelOutletSelection = () => {
    setShowConfirmDialog(false);
    setSelectedOutlet(null);
    setIsEventCheckIn(false);
    setEventDetails("");
    setIsNewOutlet(false);
    setNewOutletName("");
    setOutletSearch("");
    setOutletError(null);
    setCheckInState(sessionStatus === SESSION_STATUS.BREAK ? "BREAK" : "IDLE");
    dispatch(clearError());
  };

  const handleConfirmCheckIn = async () => {
    if (!selectedOutlet && !isNewOutlet) return;
    if (isNewOutlet && newOutletName.trim() === "") return;
    if (isMerchandiserUser && !selectedMmTask) {
      setOutletError(
        t("se.dialog.mmTaskRequired", {
          defaultValue: "Please select an MM task before checking in.",
        }),
      );
      return;
    }
    try {
      dispatch(setLoading(true));
      setLoadingLoc(true);
      setOutletError(null);

      const selectedOutletId = toOptionalPositiveInt(
        selectedOutlet?.bp_outlet_id ?? selectedOutlet?.id,
      );

      // Merchandiser sessions carry an MM task; the roadshow flag stays off.
      // Promoters keep the existing roadshow behaviour untouched.
      const visitTypeFields = isMerchandiserUser
        ? {
            is_event: false,
            event_details: "",
            mm_task: selectedMmTask,
          }
        : {
            is_event: isEventCheckIn,
            event_details: isEventCheckIn
              ? eventDetails.trim() || DEFAULT_ROADSHOW_EVENT_DETAILS
              : "",
          };

      const payload = isNewOutlet
        ? {
            outlet_id: null,
            outlet_name: newOutletName.trim(),
            outlet_address: currentLocation?.address || "",
            resume_from_check_in_id:
              sessionStatus === SESSION_STATUS.BREAK
                ? resumeCandidate?.check_in_id || null
                : null,
            check_in_latitude: currentLocation?.lat || null,
            check_in_longitude: currentLocation?.lng || null,
            check_in_address: currentLocation?.address || null,
            check_in_accuracy_meters: currentLocation?.accuracy ?? null,
            location_captured_at: currentLocation?.capturedAt || null,
            ...visitTypeFields,
          }
        : {
            outlet_id: selectedOutletId,
            outlet_name: selectedOutlet.name || selectedOutlet.bp_outlet_name,
            outlet_address: selectedOutlet.address || "",
            resume_from_check_in_id:
              sessionStatus === SESSION_STATUS.BREAK
                ? resumeCandidate?.check_in_id || null
                : null,
            check_in_latitude: currentLocation?.lat || null,
            check_in_longitude: currentLocation?.lng || null,
            check_in_address: currentLocation?.address || null,
            check_in_accuracy_meters: currentLocation?.accuracy ?? null,
            location_captured_at: currentLocation?.capturedAt || null,
            ...visitTypeFields,
          };

      const result = await createCheckIn(payload);
      const createdCheckIn = result?.checkIn || result?.data;

      if (!createdCheckIn) {
        throw new Error("Check-in response is missing data");
      }

      applySessionPayload(result);
      setActiveVisit(toVisitState(createdCheckIn, currentLocation));
      setShowConfirmDialog(false);
      setSelectedOutlet(null);
      setIsNewOutlet(false);
      setNewOutletName("");
      setIsEventCheckIn(false);
      setEventDetails("");
      setSelectedMmTask("");
      await loadTodayStats();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to check in. Please try again.";
      setOutletError(apiMessage);
    } finally {
      dispatch(setLoading(false));
      setLoadingLoc(false);
    }
  };

  const handleCancelCheckIn = () => {
    setShowConfirmDialog(false);
    setSelectedOutlet(null);
    setIsEventCheckIn(false);
    setEventDetails("");
    setSelectedMmTask("");
    setIsNewOutlet(false);
    setNewOutletName("");
    setOutletError(null);
    dispatch(clearError());
  };

  const openCheckoutDialog = () => {
    if (!activeCheckIns?.check_in_id || loadingLoc) return;
    setCheckoutReason("");
    setCheckoutErrorMessage("");
    setShowCheckoutDialog(true);
  };

  const closeCheckoutDialog = () => {
    if (loadingLoc) return;
    setShowCheckoutDialog(false);
    setCheckoutReason("");
    setCheckoutErrorMessage("");
  };

  const handleCheckOut = async () => {
    if (!activeCheckIns?.check_in_id) {
      applySessionPayload({
        success: true,
        session_status: SESSION_STATUS.IDLE,
      });
      setActiveVisit(null);
      return;
    }

    if (!checkoutReason) {
      setCheckoutErrorMessage("Please select a checkout reason.");
      return;
    }

    dispatch(setLoading(true));
    setLoadingLoc(true);

    let checkoutLatitude = null;
    let checkoutLongitude = null;
    let checkoutAddress = null;

    try {
      if (navigator.geolocation) {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        });

        const { latitude, longitude } = position.coords;
        checkoutLatitude = Number(latitude.toFixed(LOCATION_COORDINATE_DECIMALS));
        checkoutLongitude = Number(longitude.toFixed(LOCATION_COORDINATE_DECIMALS));
        checkoutAddress = await reverseGeocodeLocation(checkoutLatitude, checkoutLongitude);
      }
    } catch (locationError) {
      console.warn("Checkout location detection failed, continuing without location");
      checkoutLatitude = null;
      checkoutLongitude = null;
      checkoutAddress = null;
    }

    try {
      const response = await checkoutCheckIn(activeCheckIns.check_in_id, {
        checkout_reason: checkoutReason,
        checkout_latitude: checkoutLatitude,
        checkout_longitude: checkoutLongitude,
        checkout_address: checkoutAddress,
      });

      applySessionPayload(response);
      setView("DASHBOARD");
      setShowCheckoutDialog(false);
      setCheckoutReason("");
      setCheckoutErrorMessage("");
      await loadTodayStats();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to check out. Please try again.";
      setCheckoutErrorMessage(apiMessage);
    } finally {
      dispatch(setLoading(false));
      setLoadingLoc(false);
    }
  };

  const getStatusText = () => {
    switch (checkInState) {
      case "IDLE":
        return t("se.status.idle");
      case "LOCATING":
        return t("se.status.locating");
      case "ERROR_GPS":
        return t("se.status.errorGps");
      case "ERROR_NO_OUTLETS":
        return t("se.status.errorNoOutlets");
      case "SELECT_OUTLET":
        return t("se.status.selectOutlet");
      case "CHECKED_IN":
        return (
          <div className="se-checked-in-badge">
            <span className="se-pulse-dot"></span>
            {t("se.status.checkedIn")}
          </div>
        );
      case "BREAK":
        return t("se.status.onBreak");
      default:
        return t("se.status.idle");
    }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const getElapsedSeconds = () => {
    if (!activeCheckIns) return pausedDurationSeconds;

    const accumulatedSeconds =
      Number.parseInt(activeCheckIns.accumulated_duration_seconds || 0, 10) || 0;
    const start = getCheckInStartTimestamp(activeCheckIns);
    if (!start) {
      return (
        Number.parseInt(activeCheckIns.total_work_duration_seconds || 0, 10) ||
        accumulatedSeconds
      );
    }
    const effectiveStart = Math.min(start, timerNow);
    return Math.max(
      accumulatedSeconds,
      accumulatedSeconds + Math.floor((timerNow - effectiveStart) / 1000),
    );
  };

  const resumeLocationOption = resumeCandidate
    ? {
        id:
          resumeCandidate.outlet_id ||
          `resume-${resumeCandidate.check_in_id || "last-location"}`,
        bp_outlet_id: resumeCandidate.outlet_id || null,
        name: resumeCandidate.outlet_name || t("se.dashboard.lastLocation"),
        bp_outlet_name:
          resumeCandidate.outlet_name || t("se.dashboard.lastLocation"),
        address: resumeCandidate.outlet_address || "",
        isLastLocation: true,
      }
    : null;

  const visibleOutlets = resumeLocationOption
    ? [
        resumeLocationOption,
        ...outlets.filter((outlet) => {
          const outletId = outlet.id || outlet.bp_outlet_id || null;
          const resumeId = resumeLocationOption.bp_outlet_id || null;

          if (resumeId && outletId && resumeId === outletId) return false;

          return (
            String(outlet.name || outlet.bp_outlet_name || "")
              .trim()
              .toLowerCase() !==
            String(resumeLocationOption.name || "")
              .trim()
              .toLowerCase()
          );
        }),
      ]
    : outlets;

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (userLoading) {
    return <FullPageLoader label={t("se.loading")} />;
  }

  if (!user) {
    return <div className="error-screen">{t("se.errorLoad")}</div>;
  }
  return (
    <div className="sales-executive-container">
      {/* Mobile Header - YELLOW Theme */}
      <div className="se-header">
        <div className="se-header-title">
          {shouldShowSalesBackButton && (
            <button
              type="button"
              onClick={() => setView("DASHBOARD")}
              className="se-back-btn"
              aria-label={t("se.saleDetails.goBack", { defaultValue: "Go back" })}
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <span className="se-header-title-text">{getHeaderTitle()}</span>
        </div>
        <button onClick={handleLogout} className="se-logout-btn">
          <LogOut size={20} />
        </button>
      </div>

      <div className="se-content">
        {view === "DASHBOARD" && (
          <>
            <div className={`se-welcome ${!welcomeVisible ? "fade-out" : ""}`}>
              <h2 className="se-welcome-title">
                {t("se.welcome.title", { name: user?.full_name || "User" })}
              </h2>
              <p className="se-welcome-date">{getCurrentDate()}</p>
              <div className="se-stats-grid">
                {loadingTodayStats ? (
                  <>
                    <div
                      className="se-stat-card se-stat-skeleton"
                      aria-hidden="true"
                    >
                      <div className="se-stat-skeleton-label"></div>
                      <div className="se-stat-skeleton-value"></div>
                    </div>
                    <div
                      className="se-stat-card se-stat-skeleton"
                      aria-hidden="true"
                    >
                      <div className="se-stat-skeleton-label"></div>
                      <div className="se-stat-skeleton-value"></div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="se-stat-card">
                      <span className="se-stat-label">
                        {t("se.dashboard.salesToday")}
                      </span>
                      <span className="se-stat-value">{todayStats.sales}</span>
                    </div>
                    <div className="se-stat-card">
                      <span className="se-stat-label">
                        {t("se.dashboard.visits")}
                      </span>
                      <span className="se-stat-value">{todayStats.visits}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {successMessage && (
              <div className="se-success-message">
                <div className="success-icon">✓</div>
                <span>{successMessage}</span>
              </div>
            )}

            <div className="se-status-card">
              <div className="se-status-header">
                <div className="se-status-title">
                  <h3>{t("se.dashboard.currentStatus")}</h3>
                  <span className="se-status-text">{getStatusText()}</span>
                </div>
                {checkInState === "SELECT_OUTLET"}
              </div>

              <div className="se-status-body">
                {/* STATE: IDLE (CIRCULAR BUTTON) */}
                {loadingActiveCheckIn && checkInState === "IDLE" && (
                  <div className="se-checked-in">
                    <div className="se-timer-skeleton" aria-hidden="true">
                      <div className="se-timer-skeleton-bar"></div>
                    </div>
                    <div className="se-checked-in-location">
                      <MapPin size={14} className="se-location-pin" />
                      <span>Restoring active session...</span>
                    </div>
                  </div>
                )}
                {!loadingActiveCheckIn && checkInState === "IDLE" && (
                  <div className="se-idle-state">
                    <button
                      onClick={startCheckInProcess}
                      className="se-checkin-btn"
                    >
                      <MapPin size={40} className="se-checkin-icon" />
                      <span className="se-checkin-text">
                        {t("se.dashboard.checkIn")}
                      </span>
                      <span className="se-checkin-subtitle">
                        {t("se.dashboard.tapToStart")}
                      </span>
                    </button>
                  </div>
                )}
                {!loadingActiveCheckIn && checkInState === "BREAK" && (
                  <div className="se-checked-in se-break-state">
                    <div className="se-break-badge">{t("se.status.onBreak")}</div>
                    <h2 className="se-timer">{formatTime(getElapsedSeconds())}</h2>
                    <div className="se-checked-in-location">
                      <MapPin size={14} className="se-location-pin" />
                      <span>
                        {resumeCandidate?.outlet_name || t("se.dashboard.lastLocation")}
                      </span>
                    </div>
                    {resumeCandidate?.outlet_address ? (
                      <div className="se-checked-in-address">
                        <span>{resumeCandidate.outlet_address}</span>
                      </div>
                    ) : null}
                    {resumeCandidate ? (
                      <div
                        className={`se-event-badge ${
                          isRoadshowRecord(resumeCandidate) ? "is-roadshow" : "is-outlet"
                        }`}
                      >
                        <strong>
                          {isRoadshowRecord(resumeCandidate)
                            ? DEFAULT_ROADSHOW_EVENT_DETAILS
                            : t("admin.sessions.outletCheckIn")}
                        </strong>
                      </div>
                    ) : null}
                    <div className="se-break-hint">{t("se.dashboard.resumeHint")}</div>
                    <div className="se-action-buttons">
                      <button
                        onClick={startCheckInProcess}
                        className="se-btn se-btn-primary"
                      >
                        {t("se.dashboard.resume")}
                      </button>
                    </div>
                  </div>
                )}

                {/* STATE: LOCATING */}
                {checkInState === "LOCATING" && (
                  <div className="se-locating-state">
                    <div className="se-locating-spinner">
                      <div className="se-gps-icon">
                        <Navigation size={32} />
                      </div>
                      <div className="se-signal-ring se-signal-ring-1"></div>
                      <div className="se-signal-ring se-signal-ring-2"></div>
                      <div className="se-signal-ring se-signal-ring-3"></div>
                    </div>
                    <h4 className="se-locating-title">
                      {t("se.dashboard.acquiringGps")}
                    </h4>
                    <p className="se-locating-text">
                      {t("se.dashboard.standStill")}
                    </p>
                  </div>
                )}

                {/* STATE: ERROR GPS */}
                {checkInState === "ERROR_GPS" && (
                  <div className="se-error-state">
                    <div className="se-error-icon">
                      <AlertCircle size={32} />
                    </div>
                    <h4 className="se-error-title">
                      {locationErrorDetails?.title || "Location Failed"}
                    </h4>
                    <p className="se-error-text">
                      {locationErrorDetails?.message ||
                        "We couldn't detect your GPS coordinates. Please ensure location services are enabled."}
                    </p>
                    {locationErrorDetails?.help && (
                      <p className="se-error-help">
                        💡 {locationErrorDetails.help}
                      </p>
                    )}
                    {locationErrorDetails?.code && (
                      <p className="se-error-code">
                        Error Code: {locationErrorDetails.code}
                      </p>
                    )}
                    <div className="se-error-actions">
                      <button
                        onClick={startCheckInProcess}
                        className="se-retry-btn"
                      >
                        <RefreshCw size={18} /> Retry Location
                      </button>
                      <button
                        onClick={() => setCheckInState("IDLE")}
                        className="se-cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* STATE: SELECT OUTLET */}
                {checkInState === "SELECT_OUTLET" && (
                  <div className="se-select-outlet">
                    <div className="se-select-outlet-actions">
                      <button
                        type="button"
                        className="se-select-outlet-cancel-btn"
                        onClick={handleCancelOutletSelection}
                      >
                        {t("se.dialog.cancel")}
                      </button>
                    </div>
                    {/* Search Bar */}
                    <div className="se-search-container">
                      <div className="se-search-input-wrapper">
                        <Search size={18} className="se-search-icon" />
                        <input
                          type="text"
                          placeholder="Select or Search outlets by name"
                          value={outletSearch}
                          onChange={(e) => setOutletSearch(e.target.value)}
                          className="se-search-input"
                        />
                      </div>
                    </div>

                    {/* Location Info Card */}
                    <div className="se-location-content">
                      <Navigation size={12} className="se-location-nav-icon" />
                      <div>
                        <p className="se-location-address">
                          {currentLocation?.address || "Unable to locate you"}
                        </p>
                      </div>
                    </div>

                    <div className="se-outlets-list">
                      {loadingOutlets ? (
                        <div className="se-loading-state">
                          <div className="se-spinner" />
                          <p>Loading outlets...</p>
                        </div>
                      ) : visibleOutlets.length === 0 ? (
                        <>
                          <div className="se-empty-state">
                            <p>{outletError || "No Outlets Found"}</p>
                          </div>
                        </>
                      ) : (
                        visibleOutlets.map((outlet) => (
                          <div
                            key={outlet.id}
                            onClick={() => handleOutletClick(outlet)}
                            className={`se-outlet-item ${outlet.isLastLocation ? "se-outlet-item-last" : ""}`}
                          >
                            <div className="se-outlet-info">
                              {outlet.isLastLocation ? (
                                <div className="se-last-location-badge">
                                  {t("se.dashboard.lastLocation")}
                                </div>
                              ) : null}
                              <h5 className="se-outlet-name">{outlet.name}</h5>
                              <p className="se-outlet-address">
                                {outlet.address}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* New Outlet Button */}
                    <div
                      className="se-new-outlet-container"
                      style={{ marginTop: "15px", textAlign: "center" }}
                    >
                      <button
                        className="se-btn se-btn-secondary"
                        style={{ width: "100%" }}
                        onClick={() => {
                          setIsNewOutlet(true);
                          setShowConfirmDialog(true);
                        }}
                      >
                        New Outlet?
                      </button>
                    </div>
                  </div>
                )}

                {/* STATE: CHECKED IN */}
                {checkInState === "CHECKED_IN" && activeCheckIns && (
                  <div className="se-checked-in">
                    <h2 className="se-timer">{formatTime(getElapsedSeconds())}</h2>
                    <div className="se-checked-in-location">
                      <MapPin size={14} className="se-location-pin" />
                      <span>{activeCheckIns.outlet_name}</span>
                    </div>
                    <div className="se-checked-in-address">
                      <span>{activeCheckIns.outlet_address}</span>
                    </div>
                    {isRoadshowRecord(activeCheckIns) && (
                      <div className="se-event-badge">
                        <strong>{DEFAULT_ROADSHOW_EVENT_DETAILS}</strong>
                      </div>
                    )}

                    <div className="se-action-buttons">
                      <button
                        onClick={() => {
                          setSalesMode("sale");
                          setSalesEntrySource("dashboard");
                          setView("SALES");
                        }}
                        className="se-btn se-btn-primary"
                      >
                        {t("se.dashboard.recordNewSale")}
                      </button>
                      <button
                        onClick={() => {
                          setSalesMode("return");
                          setSalesEntrySource("dashboard");
                          setView("SALES");
                        }}
                        className="se-btn se-btn-return"
                      >
                        {t("se.dashboard.recordReturnSale")}
                      </button>
                      <button
                        onClick={openCheckoutDialog}
                        disabled={loadingLoc}
                        className="se-btn se-btn-secondary"
                      >
                        {loadingLoc
                          ? t("se.dashboard.acquiringLocation")
                          : t("se.dashboard.checkOut")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {view === "HISTORY" && <HistoryView />}
        {view === "SALES" && (
          <div className="se-sales-page">
            <div
              className="se-sales-toggle"
              role="tablist"
              aria-label={t("se.sales.modeSelector")}
            >
              <button
                type="button"
                role="tab"
                aria-selected={salesMode === "sale"}
                className={`se-sales-toggle-btn ${
                  salesMode === "sale" ? "active" : ""
                }`}
                onClick={() => setSalesMode("sale")}
              >
                {t("se.sales.recordSaleTab")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={salesMode === "return"}
                className={`se-sales-toggle-btn ${
                  salesMode === "return" ? "active" : ""
                }`}
                onClick={() => setSalesMode("return")}
              >
                {t("se.sales.recordReturnTab")}
              </button>
            </div>

            <RecordSale
              outlet={activeCheckIns}
              mode={salesMode}
              embedded
              onSave={({ cancelled, mode }) => {
                if (cancelled) {
                  setView("DASHBOARD");
                  return;
                }

                setView("DASHBOARD");
                setSalesMode(mode);
                setSuccessMessage(null);
                loadTodayStats();
              }}
            />
          </div>
        )}
        {view === "PROFILE" && (
          <ProfileView user={user} onLogout={handleLogout} />
        )}
        {view === "DISPLAY_PROOF_TAB" && (
          // No active check-in required: RecordDisplayProof resolves the outlet
          // from eligible visits first, mirroring the sales submission flow.
          <RecordDisplayProof
            onCancel={() => setView("DASHBOARD")}
            onSave={() => {
              setView("DASHBOARD");
              setSuccessMessage(null);
              showSuccess(t("se.displayProof.successSubmitted"));
            }}
          />
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (selectedOutlet || isNewOutlet) && (
        <div className="se-dialog-overlay">
          <div className="se-dialog">
            <div className="se-dialog-header">
              <h3 className="se-dialog-title">
                {t("se.dialog.confirmCheckIn")}
              </h3>
              <button onClick={handleCancelCheckIn} className="se-dialog-close">
                ×
              </button>
            </div>
            <div className="se-dialog-body">
              {isNewOutlet ? (
                <>
                  <p className="se-dialog-message">
                    Please enter the name of the new outlet:
                  </p>
                  <input
                    type="text"
                    className="se-search-input"
                    style={{
                      width: "100%",
                      marginTop: "10px",
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                    placeholder="New Outlet Name"
                    value={newOutletName}
                    onChange={(e) => setNewOutletName(e.target.value)}
                    autoFocus
                  />
                </>
              ) : (
                <>
                  <p className="se-dialog-message">
                    Are you sure you want to check in to this outlet?
                  </p>
                  <div className="se-dialog-outlet-info">
                    <div className="se-dialog-outlet-name">
                      {selectedOutlet?.name}
                    </div>
                    <div className="se-dialog-outlet-address">
                      {selectedOutlet?.address}
                    </div>
                  </div>
                </>
              )}
            </div>

            {isMerchandiserUser ? (
              <div className="se-mm-task-wrapper">
                <label className="se-mm-task-label" htmlFor="se-mm-task">
                  {t("se.dialog.mmTaskLabel", { defaultValue: "MM Task" })}
                  <span className="se-mm-task-required" aria-hidden="true">
                    {" *"}
                  </span>
                </label>
                <select
                  id="se-mm-task"
                  className="se-mm-task-select"
                  value={selectedMmTask}
                  onChange={(event) => {
                    setSelectedMmTask(event.target.value);
                    setOutletError(null);
                  }}
                  disabled={loadingMmTaskTypes || loadingLoc}
                >
                  <option value="">
                    {loadingMmTaskTypes
                      ? t("se.dialog.mmTaskLoading", {
                          defaultValue: "Loading tasks...",
                        })
                      : t("se.dialog.mmTaskPlaceholder", {
                          defaultValue: "Select an MM task",
                        })}
                  </option>
                  {mmTaskTypes.map((taskType) => (
                    <option key={taskType.mm_task_type_id} value={taskType.code}>
                      {taskType.name}
                    </option>
                  ))}
                </select>
                {mmTaskTypesError ? (
                  <p className="se-mm-task-error">{mmTaskTypesError}</p>
                ) : (
                  <p className="se-mm-task-help">
                    {t("se.dialog.mmTaskHelper", {
                      defaultValue:
                        "Select the task you are performing at this location.",
                    })}
                  </p>
                )}
              </div>
            ) : (
              <div className="se-event-link-wrapper">
                <label className="se-roadshow-toggle">
                  <input
                    type="checkbox"
                    checked={isEventCheckIn}
                    onChange={(event) => {
                      const nextChecked = event.target.checked;
                      setIsEventCheckIn(nextChecked);
                      if (!nextChecked) {
                        setEventDetails("");
                      }
                    }}
                  />
                  <span>
                    {t("se.dialog.roadshowToggle", {
                      defaultValue: "Mark this check-in as Roadshow",
                    })}
                  </span>
                </label>
                {isEventCheckIn ? (
                  <p className="se-roadshow-toggle-help">
                    {t("se.dialog.roadshowNotesHelper", {
                      defaultValue:
                        "Optional: add notes. If left blank, this session is tagged as Roadshow.",
                    })}
                  </p>
                ) : null}
              </div>
            )}
            {!isMerchandiserUser && isEventCheckIn && (
              <div className="se-event-panel">
                <div className="se-event-panel-header">
                  <strong>
                    {t("se.dialog.roadshowNotesLabel", {
                      defaultValue: "Roadshow Notes (Optional)",
                    })}
                  </strong>
                </div>
                <textarea
                  className="se-event-input"
                  placeholder={t("se.dialog.roadshowNotesPlaceholder", {
                    defaultValue: "Enter event name or notes (optional)",
                  })}
                  value={eventDetails}
                  onChange={(e) => setEventDetails(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleConfirmCheckIn();
                    }
                  }}
                />
              </div>
            )}
            {outletError && (
              <div
                style={{
                  color: "#c0392b",
                  background: "#fdecea",
                  border: "1px solid #f5c6cb",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  margin: "8px 0",
                  fontSize: "0.875rem",
                }}
              >
                {outletError}
              </div>
            )}
            <div className="se-dialog-actions">
              <button
                onClick={handleCancelCheckIn}
                className="se-dialog-btn se-dialog-btn-cancel"
                disabled={loadingLoc}
              >
                {t("se.dialog.cancel")}
              </button>
              <button
                onClick={handleConfirmCheckIn}
                className="se-dialog-btn se-dialog-btn-confirm"
                disabled={
                  loadingLoc ||
                  (isNewOutlet && newOutletName.trim() === "") ||
                  (isMerchandiserUser && !selectedMmTask)
                }
              >
                {loadingLoc
                  ? t("se.dialog.checkingIn")
                  : t("se.dialog.confirmCheckInBtn")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCheckoutDialog && activeCheckIns && (
        <div className="se-dialog-overlay">
          <div className="se-dialog">
            <div className="se-dialog-header">
              <h3 className="se-dialog-title">
                {t("se.dialog.confirmCheckOut")}
              </h3>
              <button
                onClick={closeCheckoutDialog}
                className="se-dialog-close"
                disabled={loadingLoc}
              >
                ×
              </button>
            </div>

            <div className="se-dialog-body">
              <p className="se-dialog-message">{t("se.dialog.selectReason")}</p>
              <div className="se-checkout-reason-group">
                <label className="se-checkout-reason-option">
                  <input
                    type="radio"
                    name="checkout_reason"
                    value="break"
                    checked={checkoutReason === "break"}
                    onChange={(event) => {
                      setCheckoutReason(event.target.value);
                      setCheckoutErrorMessage("");
                    }}
                    disabled={loadingLoc}
                  />
                  <span>{t("se.checkout.break")}</span>
                </label>
                <label className="se-checkout-reason-option">
                  <input
                    type="radio"
                    name="checkout_reason"
                    value="off_duty"
                    checked={checkoutReason === "off_duty"}
                    onChange={(event) => {
                      setCheckoutReason(event.target.value);
                      setCheckoutErrorMessage("");
                    }}
                    disabled={loadingLoc}
                  />
                  <span>{t("se.checkout.offDuty")}</span>
                </label>
              </div>
            </div>

            {checkoutErrorMessage && (
              <div className="se-checkout-error-message">
                {checkoutErrorMessage}
              </div>
            )}

            <div className="se-dialog-actions">
              <button
                onClick={closeCheckoutDialog}
                className="se-dialog-btn se-dialog-btn-cancel"
                disabled={loadingLoc}
              >
                {t("se.dialog.cancel")}
              </button>
              <button
                onClick={handleCheckOut}
                className="se-dialog-btn se-dialog-btn-confirm"
                disabled={loadingLoc}
              >
                {loadingLoc
                  ? t("se.dialog.checkingOut")
                  : t("se.dialog.confirmCheckOutBtn")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <div className="se-bottom-nav">
        <button
          onClick={() => setView("DASHBOARD")}
          className={`se-nav-btn ${
            view === "DASHBOARD" || view === "DISPLAY_PROOF"
              ? "active"
              : ""
          }`}
        >
          <BarChart2 size={24} />
          <span>{t("se.nav.dashboard")}</span>
        </button>
        <button
          onClick={() => {
            setSalesEntrySource("nav");
            setView("SALES");
          }}
          className={`se-nav-btn ${view === "SALES" ? "active" : ""}`}
        >
          <ShoppingBag size={24} />
          <span>{t("se.nav.sales")}</span>
        </button>
        <button
          onClick={() => setView("HISTORY")}
          className={`se-nav-btn ${view === "HISTORY" ? "active" : ""}`}
        >
          <Clock size={24} />
          <span>{t("se.nav.history")}</span>
        </button>
        <button
        onClick={() => setView("DISPLAY_PROOF_TAB")}
        className={`se-nav-btn ${view === "DISPLAY_PROOF_TAB" ? "active" : ""}`}
      >
        <MapPin size={24} />
        <span>{t("se.nav.displayProof", { defaultValue: "Display Proof" })}</span>
      </button>
        <button
          onClick={() => setView("PROFILE")}
          className={`se-nav-btn ${view === "PROFILE" ? "active" : ""}`}
        >
          <User size={24} />
          <span>{t("se.nav.profile")}</span>
        </button>
      </div>
    </div>
  );
}
